<template>
    <div>
        <div
            v-if="hasError"
            class="text-center">
            <h3>No data available at this time.</h3>
        </div>
        <div v-else>
            <div
                v-for="(item, index) in data"
                :key="index"
                class="dat-row-inner lp-placement">
                <div
                    v-if="item.adCopy"
                    class="blue-bg-note visible-xs">
                    <p>{{ item.adCopy }}</p>
                </div>
                <div class="left-col-wrap">
                    <div class="dat-row">
                        <div class="lender fixed-height">
                            <img :src="`${defaultLogoPath}${item.account.bankUrl}.png`" />
                            <span class="data-insurer-note">Member {{ item.bank.sourceName }}</span>
                        </div>
                        <div class="account-details-div">
                            <span
                                v-if="item.account.bankName"
                                class="bank-name">{{ item.account.bankName }}</span>
                            <span
                                v-if="item.account.name"
                                class="account-name">{{ item.account.name }}</span>
                            <div
                                v-if="bankHealth === 'yes'"
                                class="bank-health">
                                <span class="bank-health-text">
                                    <span>Bank Health</span>
                                    <div class="ad-disclosure-inner da-tooltip-span">
                                        <button
                                            id="bank-health-btn"
                                            class="tooltip-btn a11y-modal-trigger"
                                            data-target="bank-health-tooltip"></button>
                                        <div class="tooltip-box">
                                            <tooltip-view
                                                :label="bankHealthTooltip"
                                                :show-on-over="ShowOnOver"
                                                :popper-content="bankHealthTooltipDescription"
                                            />
                                        </div>
                                    </div>
                                </span>
                                <div class="text-center bankHealthIndicator">
                                    <div
                                        class="wrapperBankHealth"
                                        v-html="healthIndicator(item.bank.healthGrade)">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col right-col-wrap">
                    <div
                        v-if="item.adCopy"
                        class="blue-bg-note hidden-xs">
                        <p>{{ item.adCopy }}</p>
                    </div>
                    <div class="dat-result-data">
                        <div class="col-12 col-sm-9 col-md-9 col-lg-9">
                            <div class="dat-result-row mr-lg-2">
                                <div
                                    v-if="item.account.bonusAmount && bonusAmount === true"
                                    class="col-12 col-md-4 col-lg-3 dat-block-data">
                                    <span class="da-apy-span attr-label">
                                        <span class="attr-label">Bonus</span>
                                    </span>
                                    <span class="attr">${{ item.account.bonusAmount }}</span>
                                </div>
                                <div
                                    v-else
                                    class="col-12 col-md-4 col-lg-3 dat-block-data">
                                    <span class="da-apy-span attr-label">
                                        <span class="attr-label">APY</span>
                                        <div class="ad-disclosure-inner da-tooltip-span">
                                            <button
                                                id="apy-btn"
                                                class="tooltip-btn a11y-modal-trigger"
                                                data-target="apy-tooltip"></button>
                                            <div class="tooltip-box">
                                                <tooltip-view
                                                    :label="apyTooltip"
                                                    :show-on-over="ShowOnOver"
                                                    :popper-content="getApyTooltipContent(item)"
                                                />
                                            </div>
                                        </div>
                                    </span>
                                    <span class="attr">
                                        <template v-if="item.account.apyPrefix"><span class="prefix">{{ item.account.apyPrefix }}</span>&nbsp;</template><span class="apy">{{ precisionValue(item.account.apy) }}%</span>
                                    </span>
                                </div>
                                <div class="col-12 col-md-3 col-lg-3 dat-block-data">
                                    <span class="attr-label">Min to Earn<span v-if="isAmEx(item)">*</span></span>
                                    <span class="attr">${{ item.account.min === null ? 0 : minToEarn(item.account.min) }}</span>
                                </div>
                                <div
                                    v-if="item.account.termInDays !== null"
                                    class="col-12 col-md-5 col-lg-5 dat-block-data">
                                    <span class="attr-label">Term</span>
                                    <span class="attr">{{ termsInDays(item.account.termInDays) }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="col-12 col-sm-3 col-md-3 col-lg-3 dat-cta">
                            <div class="dat-cta-inner">
                                <button
                                    class="btn btn-blue dat-view-btn"
                                    @click.prevent="sendFormData(item.trackedLink)">
                                    {{ viewButtonText }}
                                </button>
                                <span
                                    v-if="item.account.bankNameOverride"
                                    class="text-under-btn">at {{ item.account.bankNameOverride }} site</span>
                                <span
                                    v-else
                                    class="text-under-btn">at {{ item.account.bankName }} site</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="isAmEx(item)"
                    class="disclaimer">
                    * There is no minimum balance required to open your Account, to avoid being charged a fee, or to obtain the Annual Percentage Yield (APY) disclosed to you.
                </div>
            </div>
        </div>
        <form
            id="submitForm"
            :action="getWWWUrl()"
            method="Post"
            target="_blank">
            <input
                v-model="trackedLink"
                type="hidden"
                name="trackedLink"
            />
        </form>
    </div>
</template>
<script>
    import Accounting from 'accounting';
    import tooltipView from '~/components/tooltip-view.vue';
    import depositPaymentHelper from '~/helpers/deposit-account-api';
    import wwwUrlEnv from '~/helpers/www-url-env';

    export default {
        components: {
            tooltipView,
        },
        props: {
            data: {
                type: Array,
                default: () => [],
            },
            hasError: {
                type: Boolean,
            },
            bonusAmount: {
                type: Boolean,
                default: false,
            },
            showOnOver: {
                type: Boolean,
                default: true,
            },
            bankHealthTooltip: {
                type: String,
                default: '',
            },
            bankHealthTooltipDescription: {
                type: String,
                default: 'The LendingTree proprietary health rating system is an unbiased indicator of the overall financial health of the institution. An institution with a low score may be at risk of failure.',
            },
            apyTooltip: {
                type: String,
                default: '',
            },
            apyTooltipDescription: {
                type: String,
                default: 'APY = Annual Percentage Yield Rates as of ',
            },
            viewButtonText: {
                type: String,
                default: 'View Details',
            },
            bankHealth: {
                type: String,
                default: 'yes',
            },
        },
        data() {
            return {
                trackedLink: '',
                ShowOnOver: this.showOnOver,
                ShowDisclosure: false,
                defaultLogoPath: 'https://www.depositaccounts.com/content/spnsr/125x70/',
            };
        },
        methods: {
            setDisclosureShow() {
                setTimeout(() => {
                    this.ShowDisclosure = true;
                }, 500);
            },
            healthIndicator(healthGrade) {
                const healthGrades = {
                    Good: ['A+', 'A', 'B+', 'B'],
                    OK: ['C+', 'C', 'C-'],
                    Bad: ['D', 'F'],
                };
                return [
                    depositPaymentHelper.renderHealthGradeItem(healthGrade, healthGrades.Bad.includes(healthGrade), 'bad-health'),
                    depositPaymentHelper.renderHealthGradeItem(healthGrade, healthGrades.OK.includes(healthGrade), 'ok-health'),
                    depositPaymentHelper.renderHealthGradeItem(healthGrade, healthGrades.Good.includes(healthGrade), 'good-health'),
                ].join('');
            },
            termsInDays(data) {
                return depositPaymentHelper.convertDaysToMonths(data);
            },
            minToEarn(data) {
                return depositPaymentHelper.numFormatter(data);
            },
            precisionValue(value) {
                return Accounting.formatMoney(value, { symbol: '', precision: 2 });
            },
            sendFormData(value) {
                this.trackedLink = value;
                this.$nextTick(() => {
                    document.getElementById('submitForm').submit();
                });
            },
            getWWWUrl() {
                return `${wwwUrlEnv.getWWWURL()}/interstitial/banking/`;
            },
            isAmEx(item) {
                // Esoterically, 791 is the bankId for American Express
                return item.account.bankId === 791;
            },
            getApyTooltipContent(item) {
                if (this.isAmEx(item)) {
                    const apy = this.precisionValue(item.account.apy);
                    const date = depositPaymentHelper.getFormattedDate();
                    return `Annual Percentage Yield ${apy}% (APY) as advertised is accurate as of ${date}. Interest rate and APY are subject to change at any time without notice before and after a High Yield Savings Account is opened.`;
                }
                return this.apyTooltipDescription + depositPaymentHelper.getFormattedDate();
            },
        },
    };
</script>
